import { configuration } from '@src/entities/Event/includes/configuration';
import { DefaultEventEntity, EventActions, EventEntity, SaveEventPayload } from '@src/entities/Event/includes/types';
import { useSaveEvent } from '@src/entities/Event/ui/hooks';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Modal as ModalComponent } from '@uhg-abyss/web/ui/Modal';
import { isEmpty } from 'lodash';
import React from 'react';

import { Form } from '../Form';

type ModalProps = {
  action?: EventActions;
  currentEntity?: EventEntity;
  isOpen?: boolean;
  refetch?: () => void;
  setCurrentEntity?: (entity: EventEntity) => void;
  setIsOpen?: (open: boolean) => void;
};

/**
 * Modal: Event
 *
 * Displays a modal dialog containing the event form for creating or editing event data.
 * Handles modal open/close state and save logic.
 */
export function Modal({
  action,
  currentEntity,
  isOpen = false,
  refetch = () => {},
  setCurrentEntity = () => {},
  setIsOpen = () => {},
}: ModalProps) {
  const { handleSave: handleSaveEvent } = useSaveEvent(action);

  /**
   * Closes the modal and resets the current entity to default values.
   */
  const handleClose = (): void => {
    setIsOpen(false);
    setCurrentEntity(DefaultEventEntity);
  };

  /**
   * Saves the event entity and closes the modal.
   */
  const handleSave = async (payload: SaveEventPayload): Promise<void> => {
    await handleSaveEvent(payload, refetch);
    handleClose();
  };

  return (
    <ErrorHandler location="src/entities/Event/ui/components/Modal/Modal.tsx">
      <ModalComponent
        isOpen={isOpen}
        onClose={handleClose}
        size="full"
        title={!isEmpty(currentEntity) ? `Edit ${configuration.singularName}` : `Create ${configuration.singularName}`}
      >
        <ModalComponent.Section>
          <Form currentEntity={currentEntity} handleClose={handleClose} handleSave={handleSave} />
        </ModalComponent.Section>
      </ModalComponent>
    </ErrorHandler>
  );
}
